
import { useState, useEffect } from 'react';

/**
 * Хук для создания дебаунсированного значения
 * 
 * @param value Исходное значение
 * @param delay Задержка в миллисекундах
 * @returns Дебаунсированное значение
 */
export function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  
  useEffect(() => {
    // Устанавливаем таймер для обновления дебаунсированного значения
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    // Очищаем таймер при изменении исходного значения
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);
  
  return debouncedValue;
}

/**
 * Хук для предотвращения избыточных рендеров при частых обновлениях
 * 
 * @param callback Функция обратного вызова
 * @param delay Задержка в миллисекундах
 * @returns Дебаунсированная функция
 */
export function useDebounced<T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): (...args: Parameters<T>) => void {
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  
  return (...args: Parameters<T>) => {
    if (timer) {
      clearTimeout(timer);
    }
    
    setTimer(
      setTimeout(() => {
        callback(...args);
        setTimer(null);
      }, delay)
    );
  };
}
